/* eslint-disable @typescript-eslint/no-explicit-any */
import { useQuery, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import {
  AlertCircle,
  AlertTriangle,
  Building,
  CheckCircle,
  Clock,
  Eye,
  FileCheck,
  Globe,
  Loader2,
  MoreHorizontal,
  Shield,
  ShieldX,
  Users,
  XCircle,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { actions } from '../../actions';
import { services } from '../../services';
import { useUserApproval } from '../../hooks/useUserApproval';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { useToast } from '../ui/use-toast';
import KycReviewDialog from './KycReviewDialog';

interface User {
  _id: string;
  id?: string;
  name?: string;
  email?: string;
  walletAddress: string;
  profile?: string;
  status?: string;
  isKycVerified?: boolean;
  createdAt?: string;
  country?: string;
  role?: {
    roleName: string;
  };
}

interface ComplianceStats {
  statistics: {
    totalUsers: number;
    verifiedCount: number;
    pendingCount: number;
  };
  categorizedUsers: {
    verified: {
      users: User[];
      totalCount: number;
    };
    pending: {
      users: User[];
      totalCount: number;
    };
  };
}

interface KYCItem {
  id: string;
  name: string;
  wallet: string;
  submitted: string;
  documents: number;
  status: 'REVIEW' | 'PENDING' | 'APPROVED' | 'REJECTED';
  type: 'kyc' | 'reg_d' | 'reg_s' | 'accredited';
}

const hardcodedKycQueue: KYCItem[] = [
  {
    id: '1',
    name: 'Michael Chen',
    wallet: '0xF28f...50c3',
    submitted: '2024-12-17',
    documents: 3,
    status: 'REVIEW',
    type: 'kyc',
  },
  {
    id: '2',
    name: 'Anna Martinez',
    wallet: '0x9B4a...12dF',
    submitted: '2024-12-18',
    documents: 4,
    status: 'REVIEW',
    type: 'reg_d',
  },
  {
    id: '3',
    name: 'Robert Wilson',
    wallet: '0x3C7e...89aB',
    submitted: '2024-12-19',
    documents: 2,
    status: 'PENDING',
    type: 'reg_s',
  },
  {
    id: '4',
    name: 'Emily Davis',
    wallet: '0x2F8c...34eD',
    submitted: '2024-12-20',
    documents: 3,
    status: 'PENDING',
    type: 'kyc',
  },
  {
    id: '5',
    name: 'James Thompson',
    wallet: '0x7D1e...92bC',
    submitted: '2024-12-19',
    documents: 5,
    status: 'REVIEW',
    type: 'accredited',
  },
  {
    id: '6',
    name: 'Sarah Johnson',
    wallet: '0x4A2f...67dE',
    submitted: '2024-12-18',
    documents: 4,
    status: 'APPROVED',
    type: 'reg_d',
  },
  {
    id: '7',
    name: 'David Brown',
    wallet: '0x8B3c...45fA',
    submitted: '2024-12-17',
    documents: 3,
    status: 'PENDING',
    type: 'reg_s',
  },
];

const ITEMS_PER_PAGE = 10;
const KYC_SCROLL_PAGE_SIZE = 20;

export default function CompliancePage() {
  const [activeTab, setActiveTab] = useState('all');
  const [isAdmin, setIsAdmin] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [kycQueue, setKycQueue] = useState<KYCItem[]>(hardcodedKycQueue);
  const [allUsersPage, setAllUsersPage] = useState(1);
  const [loadingUserId, setLoadingUserId] = useState<string | null>(null);
  const [kycReviewUserId, setKycReviewUserId] = useState<string | null>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { approveUser, isLoading: approvalLoading } = useUserApproval();
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const checkAdminAccess = () => {
      try {
        const userStr = localStorage.getItem('user');
        if (userStr) {
          const user = JSON.parse(userStr);
          const userRole = typeof user.role === 'object' ? user.role?.roleName : user.role;
          setIsAdmin(userRole === 'ADMIN');
        }
      } catch (error) {
        console.error('Error checking admin access:', error);
      } finally {
        setCheckingAuth(false);
      }
    };
    checkAdminAccess();
  }, []);

  // Overview stats query
  const {
    data: overviewData,
    isLoading: overviewLoading,
    error: overviewError,
  } = useQuery({
    queryKey: ['compliance-overview'],
    queryFn: () =>
      actions.getUserDashboardStats({
        page: 1,
        limit: ITEMS_PER_PAGE,
        includeUsers: true,
      }),
    refetchInterval: 30000,
  });

  // All users paginated query (for "All" tab)
  const { data: allUsersData, isLoading: allUsersLoading } = useQuery({
    queryKey: ['compliance-all-users', allUsersPage],
    queryFn: () =>
      actions.getUserDashboardStats({
        page: allUsersPage,
        limit: ITEMS_PER_PAGE,
        includeUsers: true,
      }),
    enabled: activeTab === 'all',
  });

  // Infinite scroll query for pending KYC users (for "KYC Users" tab)
  const {
    data: pendingKycData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: pendingKycLoading,
  } = useInfiniteQuery({
    queryKey: ['compliance-pending-kyc'],
    queryFn: async ({ pageParam = 1 }) => {
      const result = await actions.getUserDashboardStats({
        page: pageParam,
        limit: KYC_SCROLL_PAGE_SIZE,
        includeUsers: true,
      });
      return {
        users: result?.categorizedUsers?.pending?.users || [],
        totalCount: result?.categorizedUsers?.pending?.totalCount || 0,
        page: pageParam,
      };
    },
    getNextPageParam: (lastPage) => {
      const totalPages = Math.ceil(lastPage.totalCount / KYC_SCROLL_PAGE_SIZE);
      if (lastPage.page < totalPages) return lastPage.page + 1;
      return undefined;
    },
    initialPageParam: 1,
    enabled: activeTab === 'kyc',
  });

  // Infinite scroll observer
  const observerRef = useRef<IntersectionObserver | null>(null);
  const loadMoreRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (isFetchingNextPage) return;
      if (observerRef.current) observerRef.current.disconnect();
      observerRef.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasNextPage) {
          fetchNextPage();
        }
      });
      if (node) observerRef.current.observe(node);
    },
    [isFetchingNextPage, hasNextPage, fetchNextPage]
  );

  // Flatten infinite query pages
  const allPendingKycUsers: User[] = pendingKycData?.pages.flatMap((page) => page.users) || [];

  // KYC queue helpers
  const getFilteredItems = (type?: string) => {
    if (!type || type === 'all') return kycQueue;
    return kycQueue.filter((k) => k.type === type);
  };

  const pendingKycCount = kycQueue.filter((k) => ['REVIEW', 'PENDING'].includes(k.status)).length;
  const approvedKycCount = kycQueue.filter((k) => k.status === 'APPROVED').length;
  const rejectedKycCount = kycQueue.filter((k) => k.status === 'REJECTED').length;

  // Blockchain + backend approval
  const handleApproveUser = async (user: User) => {
    const userId = user._id || user.id || '';
    setLoadingUserId(userId);
    try {
      const result = await approveUser({
        id: userId,
        walletAddress: user.walletAddress,
        country: user.country,
        name: user.name,
      });

      if (result.success) {
        queryClient.invalidateQueries({ queryKey: ['compliance-pending-kyc'] });
        queryClient.invalidateQueries({ queryKey: ['compliance-overview'] });
        queryClient.invalidateQueries({ queryKey: ['compliance-all-users'] });
      }
    } finally {
      setLoadingUserId(null);
    }
  };

  // Backend rejection
  const handleRejectUser = async (user: User) => {
    const userId = user._id || user.id || '';
    setLoadingUserId(userId);
    try {
      const response = await services.post('/user/disableUser', {
        userId,
        status: 'REJECTED',
      });

      if (response?.status === 200) {
        toast({
          description: `User ${user.name || user.walletAddress} rejected`,
        });
        queryClient.invalidateQueries({ queryKey: ['compliance-pending-kyc'] });
        queryClient.invalidateQueries({ queryKey: ['compliance-overview'] });
        queryClient.invalidateQueries({ queryKey: ['compliance-all-users'] });
      } else {
        throw new Error('Failed to reject user');
      }
    } catch (error: any) {
      if (error?.response?.status === 401 || error?.response?.status === 403) {
        toast({ description: 'Unauthorized Request', variant: 'destructive' });
      } else {
        toast({
          description: error?.response?.data?.message || 'Failed to reject user',
          variant: 'destructive',
        });
      }
    } finally {
      setLoadingUserId(null);
    }
  };

  // Hardcoded queue actions
  const handleHardcodedApprove = (id: string) => {
    setKycQueue(kycQueue.map((k) => (k.id === id ? { ...k, status: 'APPROVED' as const } : k)));
    toast({ description: 'Application approved successfully' });
  };

  const handleHardcodedReject = (id: string) => {
    setKycQueue(kycQueue.map((k) => (k.id === id ? { ...k, status: 'REJECTED' as const } : k)));
    toast({ description: 'Application rejected', variant: 'destructive' });
  };

  const handleHardcodedReview = (item: KYCItem) => {
    toast({
      description: `Opening review for ${item.name} — ${item.documents} documents to review`,
    });
  };

  const getTypeBadge = (type: string) => {
    const badges: Record<string, { label: string; className: string }> = {
      kyc: { label: 'KYC', className: 'bg-info/20 text-info' },
      reg_d: { label: 'Reg D', className: 'bg-primary/20 text-primary' },
      reg_s: { label: 'Reg S', className: 'bg-warning/20 text-warning' },
      accredited: { label: 'Accredited', className: 'bg-success/20 text-success' },
    };
    const badge = badges[type] || badges.kyc;
    return (
      <span className={`px-2 py-0.5 rounded text-xs font-medium ${badge.className}`}>
        {badge.label}
      </span>
    );
  };

  const getInitials = (name?: string, wallet?: string) => {
    if (name)
      return name
        .split(' ')
        .map((n) => n[0])
        .join('')
        .slice(0, 2)
        .toUpperCase();
    return (wallet || '??').slice(0, 2).toUpperCase();
  };

  // Render hardcoded queue items
  const renderQueue = (items: KYCItem[]) => {
    const pendingItems = items.filter((k) => ['REVIEW', 'PENDING'].includes(k.status));
    return (
      <div className='space-y-4'>
        {pendingItems.map((item) => (
          <div
            key={item.id}
            className='flex flex-col sm:flex-row sm:items-center justify-between p-4 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors gap-4'
          >
            <div className='flex items-center gap-4'>
              <div className='flex items-center justify-center w-10 h-10 rounded-full bg-primary/20 text-primary font-semibold flex-shrink-0'>
                {item.name
                  .split(' ')
                  .map((n) => n[0])
                  .join('')}
              </div>
              <div>
                <div className='flex items-center gap-2'>
                  <p className='font-medium text-foreground'>{item.name}</p>
                  {getTypeBadge(item.type)}
                </div>
                <code className='text-xs font-mono text-muted-foreground'>{item.wallet}</code>
              </div>
            </div>
            <div className='flex items-center gap-6'>
              <div className='text-center'>
                <p className='text-sm font-medium text-foreground'>{item.documents}</p>
                <p className='text-xs text-muted-foreground'>Documents</p>
              </div>
              <div className='text-center'>
                <p className='text-sm font-medium text-muted-foreground'>{item.submitted}</p>
                <p className='text-xs text-muted-foreground'>Submitted</p>
              </div>
              <div className='flex items-center gap-2'>
                <Button size='sm' variant='secondary' onClick={() => handleHardcodedReview(item)}>
                  Review
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant='ghost' size='icon' className='h-8 w-8'>
                      <MoreHorizontal className='w-4 h-4' />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align='end'>
                    <DropdownMenuLabel>Actions</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => handleHardcodedReview(item)}>
                      <Eye className='w-4 h-4 mr-2' />
                      View Documents
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      className='text-success'
                      onClick={() => handleHardcodedApprove(item.id)}
                    >
                      <CheckCircle className='w-4 h-4 mr-2' />
                      Approve
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      className='text-destructive'
                      onClick={() => handleHardcodedReject(item.id)}
                    >
                      <XCircle className='w-4 h-4 mr-2' />
                      Reject
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </div>
        ))}
        {pendingItems.length === 0 && (
          <div className='text-center py-8 text-muted-foreground'>
            <CheckCircle className='w-12 h-12 mx-auto mb-2 text-success' />
            <p>All caught up! No pending reviews.</p>
          </div>
        )}
      </div>
    );
  };

  // Render a real user card with approve/reject
  const UserCardWithActions = ({ user }: { user: User }) => {
    const userId = user._id || user.id || '';
    const isCurrentLoading = loadingUserId === userId;
    return (
      <div className='flex flex-col sm:flex-row sm:items-center justify-between p-4 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors gap-4'>
        <div className='flex items-center gap-3 overflow-hidden'>
          <Avatar className='h-10 w-10 flex-shrink-0'>
            <AvatarImage src={user.profile} alt={user.name || 'User'} />
            <AvatarFallback className='text-xs'>
              {getInitials(user.name, user.walletAddress)}
            </AvatarFallback>
          </Avatar>
          <div className='min-w-0'>
            <div className='flex items-center gap-2'>
              <p className='font-medium text-foreground truncate'>
                {user.name || `${user.walletAddress.slice(0, 6)}...${user.walletAddress.slice(-4)}`}
              </p>
              <Badge variant='secondary' className='flex-shrink-0 text-xs px-2 py-0.5'>
                Pending
              </Badge>
            </div>
            <p className='text-xs text-muted-foreground truncate'>{user.email || 'No email'}</p>
            <code className='text-xs font-mono text-muted-foreground'>
              {user.walletAddress
                ? `${user.walletAddress.slice(0, 6)}...${user.walletAddress.slice(-4)}`
                : '—'}
            </code>
          </div>
        </div>
        <div className='flex items-center gap-6 flex-shrink-0'>
          <div className='text-center'>
            <p className='text-sm font-medium text-foreground'>—</p>
            <p className='text-xs text-muted-foreground'>Documents</p>
          </div>
          <div className='text-center'>
            <p className='text-sm font-medium text-muted-foreground'>
              {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : '—'}
            </p>
            <p className='text-xs text-muted-foreground'>Submitted</p>
          </div>
          <div className='flex items-center gap-2'>
            <Button size='sm' variant='secondary' onClick={() => setKycReviewUserId(userId)}>
              Review
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant='ghost' size='icon' className='h-8 w-8'>
                  <MoreHorizontal className='w-4 h-4' />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align='end'>
                <DropdownMenuLabel>Actions</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setKycReviewUserId(userId)}>
                  <Eye className='w-4 h-4 mr-2' />
                  View Documents
                </DropdownMenuItem>
                <DropdownMenuItem
                  className='text-success'
                  onClick={() => handleApproveUser(user)}
                  disabled={isCurrentLoading || approvalLoading}
                >
                  {isCurrentLoading ? (
                    <Loader2 className='w-4 h-4 mr-2 animate-spin' />
                  ) : (
                    <CheckCircle className='w-4 h-4 mr-2' />
                  )}
                  Approve
                </DropdownMenuItem>
                <DropdownMenuItem
                  className='text-destructive'
                  onClick={() => handleRejectUser(user)}
                  disabled={isCurrentLoading || approvalLoading}
                >
                  <XCircle className='w-4 h-4 mr-2' />
                  Reject
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
    );
  };

  if (checkingAuth) {
    return (
      <div className='flex items-center justify-center min-h-[400px]'>
        <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-primary'></div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className='flex flex-col items-center justify-center min-h-[400px] space-y-4 px-4'>
        <ShieldX className='h-16 w-16 text-destructive' />
        <div className='text-center'>
          <h2 className='text-xl font-semibold text-foreground mb-2'>Access Denied</h2>
          <p className='text-muted-foreground'>
            You need admin privileges to access the compliance dashboard.
          </p>
        </div>
      </div>
    );
  }

  if (overviewLoading) {
    return (
      <div className='flex items-center justify-center min-h-[400px]'>
        <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-primary'></div>
      </div>
    );
  }

  if (overviewError) {
    return (
      <div className='flex flex-col items-center justify-center min-h-[400px] space-y-4 px-4'>
        <AlertCircle className='h-12 w-12 text-destructive' />
        <div className='text-center space-y-2'>
          <h3 className='text-lg font-medium'>Error loading compliance data</h3>
          <p className='text-sm text-muted-foreground'>Please try again later</p>
        </div>
      </div>
    );
  }

  const stats = (overviewData as ComplianceStats) || null;
  const allUsersStats = (allUsersData as ComplianceStats) || stats;
  const allUsersList = [
    ...(allUsersStats?.categorizedUsers?.verified?.users || []),
    ...(allUsersStats?.categorizedUsers?.pending?.users || []),
  ];
  const totalAllUsers = stats?.statistics?.totalUsers || 0;
  const totalAllPages = Math.ceil(totalAllUsers / ITEMS_PER_PAGE);

  return (
    <div className='space-y-6'>
      {/* Page Header */}
      <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4'>
        <div>
          <h1 className='text-2xl font-bold text-foreground'>Compliance & KYC</h1>
          <p className='text-muted-foreground'>
            Manage KYC verification, investor accreditation, and regulatory compliance.
          </p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4'>
        <div className='glass-card rounded-xl p-6 hover:border-primary/30 transition-all duration-300'>
          <div className='flex items-center gap-3'>
            <div className='flex items-center justify-center w-12 h-12 rounded-xl bg-warning/20 text-warning'>
              <Clock className='w-6 h-6' />
            </div>
            <div>
              <p className='text-sm text-muted-foreground'>Pending Review</p>
              <p className='text-2xl font-bold text-foreground'>
                {(stats?.statistics.pendingCount || 0) + pendingKycCount}
              </p>
            </div>
          </div>
        </div>
        <div className='glass-card rounded-xl p-6 hover:border-primary/30 transition-all duration-300'>
          <div className='flex items-center gap-3'>
            <div className='flex items-center justify-center w-12 h-12 rounded-xl bg-success/20 text-success'>
              <CheckCircle className='w-6 h-6' />
            </div>
            <div>
              <p className='text-sm text-muted-foreground'>Approved (30d)</p>
              <p className='text-2xl font-bold text-foreground'>
                {(stats?.statistics.verifiedCount || 142) + approvedKycCount}
              </p>
            </div>
          </div>
        </div>
        <div className='glass-card rounded-xl p-6 hover:border-primary/30 transition-all duration-300'>
          <div className='flex items-center gap-3'>
            <div className='flex items-center justify-center w-12 h-12 rounded-xl bg-destructive/20 text-destructive'>
              <XCircle className='w-6 h-6' />
            </div>
            <div>
              <p className='text-sm text-muted-foreground'>Rejected (30d)</p>
              <p className='text-2xl font-bold text-foreground'>{3 + rejectedKycCount}</p>
            </div>
          </div>
        </div>
        <div className='glass-card rounded-xl p-6 hover:border-primary/30 transition-all duration-300'>
          <div className='flex items-center gap-3'>
            <div className='flex items-center justify-center w-12 h-12 rounded-xl bg-info/20 text-info'>
              <AlertTriangle className='w-6 h-6' />
            </div>
            <div>
              <p className='text-sm text-muted-foreground'>Expiring Soon</p>
              <p className='text-2xl font-bold text-foreground'>12</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className='space-y-4'>
        <TabsList className='grid w-full grid-cols-5'>
          <TabsTrigger value='all' className='gap-2'>
            <Users className='w-4 h-4' />
            All
          </TabsTrigger>
          <TabsTrigger value='kyc' className='gap-2'>
            <Shield className='w-4 h-4' />
            KYC Users
          </TabsTrigger>
          <TabsTrigger value='reg_d' className='gap-2'>
            <Building className='w-4 h-4' />
            Reg D
          </TabsTrigger>
          <TabsTrigger value='reg_s' className='gap-2'>
            <Globe className='w-4 h-4' />
            Reg S
          </TabsTrigger>
          <TabsTrigger value='accredited' className='gap-2'>
            <FileCheck className='w-4 h-4' />
            Accredited
          </TabsTrigger>
        </TabsList>

        {/* ALL TAB */}
        <TabsContent value='all' className='glass-card rounded-xl p-6'>
          <h3 className='text-lg font-semibold text-foreground mb-4'>
            All Verification Queue ({hardcodedKycQueue.length + totalAllUsers})
          </h3>

          <div className='space-y-3'>
            {/* Hardcoded entries rendered as same style cards */}
            {kycQueue.map((item) => (
              <div
                key={`hc-${item.id}`}
                className='flex flex-col sm:flex-row sm:items-center justify-between p-4 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors gap-4'
              >
                <div className='flex items-center gap-3 overflow-hidden'>
                  <div className='flex items-center justify-center w-10 h-10 rounded-full bg-primary/20 text-primary font-semibold flex-shrink-0 text-sm'>
                    {item.name
                      .split(' ')
                      .map((n) => n[0])
                      .join('')}
                  </div>
                  <div className='min-w-0'>
                    <div className='flex items-center gap-2'>
                      <p className='font-medium text-foreground truncate'>{item.name}</p>
                      {getTypeBadge(item.type)}
                      <span
                        className={`px-2 py-0.5 rounded text-xs font-medium flex-shrink-0 ${
                          item.status === 'APPROVED'
                            ? 'bg-success/20 text-success'
                            : item.status === 'REJECTED'
                              ? 'bg-destructive/20 text-destructive'
                              : 'bg-warning/20 text-warning'
                        }`}
                      >
                        {item.status}
                      </span>
                    </div>
                    <code className='text-xs font-mono text-muted-foreground'>{item.wallet}</code>
                  </div>
                </div>
                <div className='flex items-center gap-6 flex-shrink-0'>
                  <div className='text-center'>
                    <p className='text-sm font-medium text-foreground'>{item.documents}</p>
                    <p className='text-xs text-muted-foreground'>Documents</p>
                  </div>
                  <div className='text-center'>
                    <p className='text-sm font-medium text-muted-foreground'>{item.submitted}</p>
                    <p className='text-xs text-muted-foreground'>Submitted</p>
                  </div>
                  <div className='flex items-center gap-2'>
                    <Button
                      size='sm'
                      variant='secondary'
                      onClick={() => handleHardcodedReview(item)}
                    >
                      Review
                    </Button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant='ghost' size='icon' className='h-8 w-8'>
                          <MoreHorizontal className='w-4 h-4' />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align='end'>
                        <DropdownMenuLabel>Actions</DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => handleHardcodedReview(item)}>
                          <Eye className='w-4 h-4 mr-2' />
                          View Documents
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          className='text-success'
                          onClick={() => handleHardcodedApprove(item.id)}
                        >
                          <CheckCircle className='w-4 h-4 mr-2' />
                          Approve
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          className='text-destructive'
                          onClick={() => handleHardcodedReject(item.id)}
                        >
                          <XCircle className='w-4 h-4 mr-2' />
                          Reject
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              </div>
            ))}

            {/* API users */}
            {allUsersLoading ? (
              <div className='flex items-center justify-center py-8'>
                <Loader2 className='h-6 w-6 animate-spin text-muted-foreground' />
              </div>
            ) : (
              <>
                {allUsersList.map((user) => (
                  <div
                    key={user._id}
                    className='flex flex-col sm:flex-row sm:items-center justify-between p-4 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors gap-4'
                  >
                    <div className='flex items-center gap-3 overflow-hidden'>
                      <Avatar className='h-10 w-10 flex-shrink-0'>
                        <AvatarImage src={user.profile} alt={user.name || 'User'} />
                        <AvatarFallback className='text-xs'>
                          {getInitials(user.name, user.walletAddress)}
                        </AvatarFallback>
                      </Avatar>
                      <div className='min-w-0'>
                        <div className='flex items-center gap-2'>
                          <p className='font-medium text-foreground truncate'>
                            {user.name ||
                              `${user.walletAddress.slice(0, 6)}...${user.walletAddress.slice(-4)}`}
                          </p>
                          <span className='px-2 py-0.5 rounded text-xs font-medium bg-info/20 text-info flex-shrink-0'>
                            KYC
                          </span>
                          <Badge
                            variant={user.isKycVerified ? 'default' : 'secondary'}
                            className='flex-shrink-0 text-xs px-2 py-0.5'
                          >
                            {user.isKycVerified ? 'Verified' : 'Pending'}
                          </Badge>
                        </div>
                        <p className='text-xs text-muted-foreground truncate'>
                          {user.email || 'No email'}
                        </p>
                        <code className='text-xs font-mono text-muted-foreground'>
                          {user.walletAddress
                            ? `${user.walletAddress.slice(0, 6)}...${user.walletAddress.slice(-4)}`
                            : '—'}
                        </code>
                      </div>
                    </div>
                    <div className='flex items-center gap-6 flex-shrink-0'>
                      <div className='text-center'>
                        <p className='text-sm font-medium text-muted-foreground'>
                          {user.role?.roleName || 'User'}
                        </p>
                        <p className='text-xs text-muted-foreground'>Role</p>
                      </div>
                      <div className='text-center'>
                        <p className='text-sm font-medium text-muted-foreground'>
                          {new Date(user.createdAt || '').toLocaleDateString()}
                        </p>
                        <p className='text-xs text-muted-foreground'>Joined</p>
                      </div>
                      <div className='flex items-center gap-2'>
                        <Button
                          size='sm'
                          variant='secondary'
                          onClick={() => setKycReviewUserId(user._id || user.id || '')}
                        >
                          Review
                        </Button>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant='ghost' size='icon' className='h-8 w-8'>
                              <MoreHorizontal className='w-4 h-4' />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align='end'>
                            <DropdownMenuLabel>Actions</DropdownMenuLabel>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => setKycReviewUserId(user._id || user.id || '')}
                            >
                              <Eye className='w-4 h-4 mr-2' />
                              View Documents
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              className='text-success'
                              onClick={() => handleApproveUser(user)}
                              disabled={approvalLoading || loadingUserId === (user._id || user.id)}
                            >
                              <CheckCircle className='w-4 h-4 mr-2' />
                              Approve
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              className='text-destructive'
                              onClick={() => handleRejectUser(user)}
                              disabled={loadingUserId === (user._id || user.id)}
                            >
                              <XCircle className='w-4 h-4 mr-2' />
                              Reject
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  </div>
                ))}
                {allUsersList.length === 0 && kycQueue.length === 0 && (
                  <p className='text-center text-muted-foreground py-6 text-sm'>No users found</p>
                )}
              </>
            )}
          </div>

          {/* Pagination */}
          {totalAllPages > 1 && (
            <div className='flex items-center justify-between mt-6 pt-4 border-t border-border'>
              <p className='text-sm text-muted-foreground'>
                Page {allUsersPage} of {totalAllPages}
              </p>
              <div className='flex gap-2'>
                <Button
                  variant='outline'
                  size='sm'
                  disabled={allUsersPage <= 1}
                  onClick={() => setAllUsersPage((p) => p - 1)}
                >
                  Previous
                </Button>
                <Button
                  variant='outline'
                  size='sm'
                  disabled={allUsersPage >= totalAllPages}
                  onClick={() => setAllUsersPage((p) => p + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </TabsContent>

        {/* KYC USERS TAB — Real pending users with infinite scroll + blockchain approve/reject */}
        <TabsContent value='kyc' className='glass-card rounded-xl p-6'>
          <h3 className='text-lg font-semibold text-foreground mb-4'>
            KYC Users Queue ({stats?.statistics.pendingCount || 0} pending)
          </h3>

          {pendingKycLoading ? (
            <div className='flex items-center justify-center py-12'>
              <Loader2 className='h-6 w-6 animate-spin text-muted-foreground' />
            </div>
          ) : allPendingKycUsers.length === 0 ? (
            <div className='text-center py-8 text-muted-foreground'>
              <CheckCircle className='w-12 h-12 mx-auto mb-2 text-success' />
              <p>All caught up! No pending KYC reviews.</p>
            </div>
          ) : (
            <div
              ref={scrollContainerRef}
              className='max-h-[600px] overflow-y-auto space-y-3 pr-1 scrollbar-none'
            >
              {allPendingKycUsers.map((user) => (
                <UserCardWithActions key={user._id} user={user} />
              ))}

              {/* Infinite scroll trigger */}
              <div ref={loadMoreRef} className='py-2'>
                {isFetchingNextPage && (
                  <div className='flex items-center justify-center py-4'>
                    <Loader2 className='h-5 w-5 animate-spin text-muted-foreground' />
                    <span className='ml-2 text-sm text-muted-foreground'>Loading more...</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </TabsContent>

        {/* REG D TAB */}
        <TabsContent value='reg_d' className='glass-card rounded-xl p-6'>
          <h3 className='text-lg font-semibold text-foreground mb-4'>Reg D Investors Queue</h3>
          {renderQueue(getFilteredItems('reg_d'))}
        </TabsContent>

        {/* REG S TAB */}
        <TabsContent value='reg_s' className='glass-card rounded-xl p-6'>
          <h3 className='text-lg font-semibold text-foreground mb-4'>Reg S Investors Queue</h3>
          {renderQueue(getFilteredItems('reg_s'))}
        </TabsContent>

        {/* ACCREDITED TAB */}
        <TabsContent value='accredited' className='glass-card rounded-xl p-6'>
          <h3 className='text-lg font-semibold text-foreground mb-4'>Accredited Investors Queue</h3>
          {renderQueue(getFilteredItems('accredited'))}
        </TabsContent>
      </Tabs>

      {kycReviewUserId && (
        <KycReviewDialog
          userId={kycReviewUserId}
          open={!!kycReviewUserId}
          onOpenChange={(open) => {
            if (!open) setKycReviewUserId(null);
          }}
          onReviewComplete={() => {
            queryClient.invalidateQueries({ queryKey: ['compliance-pending-kyc'] });
            queryClient.invalidateQueries({ queryKey: ['compliance-overview'] });
            queryClient.invalidateQueries({ queryKey: ['compliance-all-users'] });
          }}
        />
      )}
    </div>
  );
}
